import { logger } from '@adornis/base/logging.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { AdornisLLMRouter } from '@adornis/llm/server/llm-router.js';
import { evaluateGradingConfidenceModel, evaluateGradingConfidenceTemperature } from '../../shared/models.js';
import { addCostToExam } from '../api/add-cost.js';
import { getConfiguredLLMRouter } from '../api/llm/llm-gate.js';
import type { AgentPayloadPart } from '../api/agents/agent-payload.js';
import type { ExamTask } from '../exam.js';

let _llmRouter: AdornisLLMRouter<any> | null = null;

function getReviewerRouter(): AdornisLLMRouter<any> {
  if (!_llmRouter) {
    // The CONFIGURED form: this one sets model and temperature once and then calls `create({ messages })`,
    // so it needs the router whose `create` merges the constructor options into the body.
    _llmRouter = getConfiguredLLMRouter({
      provider: 'openrouter',
      model: evaluateGradingConfidenceModel,
      temperature: evaluateGradingConfidenceTemperature,
    });
  }
  return _llmRouter;
}

interface ReviewResult {
  agreement: number;
  reasoning: string;
}

async function getReviewerApproval(
  taskDescription: string,
  studentText: string,
  aiExplanation: string,
  masterSolutionContent: string,
  assignedGrade: number,
  maxPoints: number,
  examId?: string,
  agentPayloadParts?: AgentPayloadPart[],
): Promise<ReviewResult> {
  try {
    const gradeLine = `VERGEBENE PUNKTZAHL (score-Feld, das allein zählt): ${assignedGrade} von ${maxPoints} Punkten`;
    const hasPayload = Array.isArray(agentPayloadParts) && agentPayloadParts.length > 0;
    const userContent: any = hasPayload
      ? [...agentPayloadParts, { type: 'text', text: `\n${gradeLine}\nBEWERTUNG DES ERSTKORREKTORS: ${aiExplanation}` }]
      : `
            AUFGABE: ${taskDescription}
            MUSTERLÖSUNG: ${masterSolutionContent}
            SCHÜLERANTWORT (extrahiert): ${studentText}
            ${gradeLine}
            BEWERTUNG DES ERSTKORREKTORS: ${aiExplanation}
          `;

    const response = await getReviewerRouter().chat.completions.create({
      response_format: { type: 'json_object' },
      messages: [
        {
          role: 'system',
          content: `Du bist ein erfahrener Zweitkorrektor in einem Prüfungsausschuss.
          Deine Aufgabe ist es, die Bewertung eines Erstkorrektors zu validieren.

          Regeln:
          1. Prüfe, ob die Begründung des Erstkorrektors basierend auf dem zur Verfügung stehenden Kontext konsistent ist.
          2. Achte auf Halluzinationen: Hat der Erstkorrektor Dinge bewertet, die gar nicht im Schülertext stehen?
          3. Gib einen Wert zwischen 0.0 (völlig unlogisch/falsch) und 1.0 (perfekt nachvollziehbar) an.
          4. Wurde die Schülerantwort im Abgleich mit Musterlösung und Bewertungsschema korrekt bewertet?
          5. KRITISCH — Score-Konsistenz-Check: Vergleiche die "VERGEBENE PUNKTZAHL" mit der in der Begründung genannten oder berechneten Punktzahl (z.B. Teilsummen, "X/Y Punkte", eine Gesamtpunktzahl am Ende). Weichen diese voneinander ab — etwa vergebene Punktzahl 0, aber die Begründung nennt/berechnet einen deutlich höheren Wert — ist das ein SCHWERWIEGENDER FEHLER unabhängig von der inhaltlichen Qualität der Begründung. Vergib in diesem Fall maximal 0.3 als "agreement" und benenne den Widerspruch explizit in "reasoning".

          Antworte AUSSCHLIESSLICH im JSON-Format:
          {
            "reasoning": "<deine kurze Begründung für die Übereinstimmung oder Kritik auf Deutsch>"
            "agreement": <number zwischen 0.0 und 1.0>,
          }`,
        },
        {
          role: 'user',
          content: userContent,
        },
      ],
    });

    logger.info(
      { cost: JSON.stringify(response?.usage?.cost), context: 'COST_TRACKING' },
      'EvalGradingConfidence Agent cost',
    );

    try {
      // Isolate side-effect cost updates so a local DB hiccup never crashes the core flow
      await addCostToExam(response, examId, 'gradingConfidenceEvaluation');
    } catch (costError) {
      logger.error({ err: costError, context: 'evaluateGradingConfidence' }, 'Failed to write metrics cost telemetry');
    }

    const content = response.choices[0]?.message.content || '{"agreement": 0.5, "reasoning": "Fehler beim Review"}';
    const parsed = JSON.parse(content);

    return {
      agreement: typeof parsed.agreement === 'number' && Number.isFinite(parsed.agreement) ? parsed.agreement : 0.5,
      reasoning: String(parsed.reasoning || 'Keine Begründung geliefert.'),
    };
  } catch (error) {
    logger.error({ err: error }, 'Fehler bei der Zweitkorrektur');
    return { agreement: 0.5, reasoning: 'Review-Prozess im Ausweichverfahren abgefangen.' };
  }
}

interface CombinedReviewResult {
  contentAgreement: number;
  languageUseAgreement: number;
  reasoning: string;
}

/**
 * Like getReviewerApproval, but validates the content grade AND a separately-graded grammar/spelling
 * grade in ONE call: two independent agreement scores, one combined reasoning text (see
 * evaluateCombinedGradingConfidence — used when a task carries a solo (1-member) LanguageUseScheme rule).
 */
async function getReviewerApprovalCombined(
  taskDescription: string,
  studentText: string,
  contentExplanation: string,
  masterSolutionContent: string,
  languageUse: { criteria: string; explanation: string },
  examId?: string,
  agentPayloadParts?: AgentPayloadPart[],
): Promise<CombinedReviewResult> {
  try {
    const hasPayload = Array.isArray(agentPayloadParts) && agentPayloadParts.length > 0;
    const userContent: any = hasPayload
      ? [
          ...agentPayloadParts,
          {
            type: 'text',
            text: `\nBEWERTUNG DES ERSTKORREKTORS (Inhalt): ${contentExplanation}\nGRAMMATIK-KRITERIEN: ${languageUse.criteria}\nBEWERTUNG DES ERSTKORREKTORS (Grammatik/Rechtschreibung): ${languageUse.explanation}`,
          },
        ]
      : `
            AUFGABE: ${taskDescription}
            MUSTERLÖSUNG: ${masterSolutionContent}
            SCHÜLERANTWORT (extrahiert): ${studentText}
            BEWERTUNG DES ERSTKORREKTORS (Inhalt): ${contentExplanation}
            GRAMMATIK-KRITERIEN: ${languageUse.criteria}
            BEWERTUNG DES ERSTKORREKTORS (Grammatik/Rechtschreibung): ${languageUse.explanation}
          `;

    const response = await getReviewerRouter().chat.completions.create({
      response_format: { type: 'json_object' },
      messages: [
        {
          role: 'system',
          content: `Du bist ein erfahrener Zweitkorrektor in einem Prüfungsausschuss.
          Deine Aufgabe ist es, ZWEI unabhängige Bewertungen des Erstkorrektors zu validieren: die inhaltliche Bewertung UND die separate Grammatik-/Rechtschreibbewertung.

          Regeln:
          1. Prüfe für BEIDE Bewertungen, ob die Begründung basierend auf dem zur Verfügung stehenden Kontext konsistent ist.
          2. Achte auf Halluzinationen: Hat der Erstkorrektor Dinge bewertet, die gar nicht im Schülertext stehen?
          3. Gib für jede der beiden Bewertungen einen EIGENEN Wert zwischen 0.0 (völlig unlogisch/falsch) und 1.0 (perfekt nachvollziehbar) an.
          4. Formuliere EINE gemeinsame Begründung, die auf beide Bewertungen eingeht.

          Antworte AUSSCHLIESSLICH im JSON-Format:
          {
            "reasoning": "<eine gemeinsame kurze Begründung für Inhalt UND Grammatik auf Deutsch>",
            "contentAgreement": <number zwischen 0.0 und 1.0>,
            "languageUseAgreement": <number zwischen 0.0 und 1.0>
          }`,
        },
        { role: 'user', content: userContent },
      ],
    });

    logger.info(
      { cost: JSON.stringify(response?.usage?.cost), context: 'COST_TRACKING' },
      'EvalGradingConfidence Combined Agent cost',
    );

    try {
      await addCostToExam(response, examId, 'gradingConfidenceEvaluationCombined');
    } catch (costError) {
      logger.error(
        { err: costError, context: 'evaluateCombinedGradingConfidence' },
        'Failed to write metrics cost telemetry',
      );
    }

    const content =
      response.choices[0]?.message.content ||
      '{"contentAgreement": 0.5, "languageUseAgreement": 0.5, "reasoning": "Fehler beim Review"}';
    const parsed = JSON.parse(content);

    return {
      contentAgreement:
        typeof parsed.contentAgreement === 'number' && Number.isFinite(parsed.contentAgreement)
          ? parsed.contentAgreement
          : 0.5,
      languageUseAgreement:
        typeof parsed.languageUseAgreement === 'number' && Number.isFinite(parsed.languageUseAgreement)
          ? parsed.languageUseAgreement
          : 0.5,
      reasoning: String(parsed.reasoning || 'Keine Begründung geliefert.'),
    };
  } catch (error) {
    logger.error({ err: error }, 'Fehler bei der kombinierten Zweitkorrektur');
    return {
      contentAgreement: 0.5,
      languageUseAgreement: 0.5,
      reasoning: 'Review-Prozess im Ausweichverfahren abgefangen.',
    };
  }
}

/**
 * Content + language-use reviewer pass in ONE call, for a task covered by a solo (1-member) LanguageUseScheme
 * rule. Returns two independent confidence numbers (content, language-use) derived from the same
 * OCR-confidence floor, and one combined reviewerFeedback text.
 */
export async function evaluateCombinedGradingConfidence(
  task: NarrowedEntity<ExamTask, { taskType: 1; description: 1 }>,
  contentExplanation: string,
  languageUse: { criteria: string; explanation: string },
  options: {
    bbConfidences: number[];
    studentText: string;
    masterSolutionContent: string;
    agentPayloadParts?: AgentPayloadPart[];
  },
  examId?: string,
) {
  try {
    const cRead = options.bbConfidences.length > 0 ? Math.min(...options.bbConfidences) : 0.4;

    const review = await getReviewerApprovalCombined(
      task.description || 'Keine Beschreibung verfügbar',
      options.studentText,
      contentExplanation,
      options.masterSolutionContent,
      languageUse,
      examId,
      options.agentPayloadParts,
    );

    const combine = (agreement: number) => {
      const divisor = cRead + agreement;
      return divisor > 0 ? (2 * cRead * agreement) / divisor : 0;
    };

    return {
      result: combine(review.contentAgreement),
      languageUseResult: combine(review.languageUseAgreement),
      reviewerFeedback: `Score (Inhalt): ${review.contentAgreement} \n Score (Grammatik): ${review.languageUseAgreement} \n Feedback: ${review.reasoning}`,
      raw: {
        ocrCertainty: cRead,
        logicCertainty: review.contentAgreement,
        languageUseLogicCertainty: review.languageUseAgreement,
        reviewerReasoning: review.reasoning,
        allBoxConfidences: options.bbConfidences,
      },
    };
  } catch (orchestrationError) {
    logger.error(
      { err: orchestrationError, context: 'evaluateCombinedGradingConfidence' },
      'Master handler execution failed safely',
    );
    return {
      result: 0.4,
      languageUseResult: 0.4,
      reviewerFeedback: `Score: 0.5 \n Feedback: Fallback execution error boundary reached.`,
      raw: {
        ocrCertainty: 0.4,
        logicCertainty: 0.5,
        languageUseLogicCertainty: 0.5,
        reviewerReasoning: 'Internal error occurred. Defaulting to fallback confidence.',
        allBoxConfidences: options?.bbConfidences || [],
      },
    };
  }
}

export async function evaluateGradingConfidence(
  task: NarrowedEntity<ExamTask, { taskType: 1; description: 1 }>,
  aiExplanation: string,
  options: {
    bbConfidences: number[];
    studentText: string;
    masterSolutionContent: string;
    grade: number;
    maxPoints: number;
    agentPayloadParts?: AgentPayloadPart[];
  },
  examid?: string,
) {
  try {
    const cRead = options.bbConfidences.length > 0 ? Math.min(...options.bbConfidences) : 0.4;

    const review = await getReviewerApproval(
      task.description || 'Keine Beschreibung verfügbar',
      options.studentText,
      aiExplanation,
      options.masterSolutionContent,
      options.grade,
      options.maxPoints,
      examid,
      options.agentPayloadParts,
    );

    const divisor = cRead + review.agreement;
    const finalResult = divisor > 0 ? (2 * cRead * review.agreement) / divisor : 0;

    return {
      result: finalResult,
      reviewerFeedback: `Score: ${review.agreement} \n Feedback: ${review.reasoning}`,
      raw: {
        ocrCertainty: cRead,
        logicCertainty: review.agreement,
        reviewerReasoning: review.reasoning,
        allBoxConfidences: options.bbConfidences,
      },
    };
  } catch (orchestrationError) {
    logger.error(
      { err: orchestrationError, context: 'evaluateGradingConfidence' },
      'Master handler execution failed safely',
    );
    return {
      result: 0.4, // Return a safe baseline fallback confidence level
      reviewerFeedback: `Score: 0.5 \n Feedback: Fallback execution error boundary reached.`,
      raw: {
        ocrCertainty: 0.4,
        logicCertainty: 0.5,
        reviewerReasoning: 'Internal error occurred. Defaulting to fallback confidence.',
        allBoxConfidences: options?.bbConfidences || [],
      },
    };
  }
}
